import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, MODAL_BREDDE, fontStr } from '@/config';
import { gameState } from '@/state/store';
import {
  RESEPSJON_INVITASJON_COOLDOWN_DAGER,
  RESEPSJON_INVITASJON_PRIS,
} from '@/state/GameState';
import type { Author } from '@/data/types';
import { animateModalGlide } from '@/ui/modalGlide';

const SHEET_WIDTH = MODAL_BREDDE;
const ROW_WIDTH = MODAL_BREDDE - 60;
const ROW_PADDING = 24;
const RAD_GAP = 16;
const RAD_HOYDE = 170;

export class ResepsjonModal {
  private layer?: Phaser.GameObjects.Container;
  private unsubscribe?: () => void;
  private scrollY = 0;
  private scrollMax = 0;

  constructor(private scene: Phaser.Scene) {}

  open(): void {
    if (this.layer) return;
    this.layer = this.scene.add.container(0, 0).setDepth(120);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    this.scene.input.on('wheel', this.onWheel, this);
    this.render();
    animateModalGlide(this.scene, this.layer);
    this.unsubscribe = gameState.subscribe(() => this.render());
  }

  close(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.scene.input.off('wheel', this.onWheel, this);
    this.scrollY = 0;
    this.layer?.destroy(true);
    this.layer = undefined;
  }

  get isOpen(): boolean {
    return !!this.layer;
  }

  private render(): void {
    if (!this.layer) return;
    while (this.layer.list.length > 1) {
      const last = this.layer.list[this.layer.list.length - 1];
      this.layer.remove(last, true);
    }

    const snap = gameState.snapshot;
    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = 96;
    const sheetH = GAME_HEIGHT - 192;

    const sheet = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, sheetH, COLORS.beige, 0.98)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.6);
    this.layer.add(sheet);

    const topBorder = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, 4, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(topBorder);

    const headerY = sheetY + 36;
    const header = this.scene.add
      .text(sheetX + ROW_PADDING, headerY, 'Resepsjon — Inviter forfatter', {
        fontFamily: FONTS.display,
        fontSize: fontStr(32),
        color: '#862736',
        wordWrap: { width: SHEET_WIDTH - ROW_PADDING * 2 - 110 },
      })
      .setOrigin(0, 0);
    this.layer.add(header);

    const beskrivelse = this.scene.add
      .text(
        sheetX + ROW_PADDING,
        headerY + 76,
        'Kaffe og en samtale gjør underverker. Inviter en av forfatterne dine til et uformelt møte.',
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(20),
          color: '#3a2e22',
          wordWrap: { width: SHEET_WIDTH - ROW_PADDING * 2 - 110 },
        },
      )
      .setOrigin(0, 0);
    this.layer.add(beskrivelse);

    const kapital = this.scene.add
      .text(
        sheetX + ROW_PADDING,
        headerY + 162,
        `Dag ${snap.dag} · ${snap.kroner.toLocaleString('nb-NO')} kr`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: '#3a2e22',
          fontStyle: '600',
        },
      )
      .setOrigin(0, 0);
    this.layer.add(kapital);

    const closeBtn = this.scene.add
      .text(sheetX + SHEET_WIDTH - ROW_PADDING, headerY - 28, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(72),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);

    const listX = sheetX + (SHEET_WIDTH - ROW_WIDTH) / 2;
    const listTop = headerY + 208;
    const listBunn = sheetY + sheetH - 32;
    const listHoyde = listBunn - listTop;

    const listContainer = this.scene.add.container(0, 0);
    this.layer.add(listContainer);

    const maske = this.scene.make.graphics({ x: 0, y: 0 }, false);
    maske.fillStyle(0xffffff);
    maske.fillRect(listX - 4, listTop, ROW_WIDTH + 8, listHoyde);
    const maskeObj = maske.createGeometryMask();
    listContainer.setMask(maskeObj);

    const forfattere = Object.values(snap.authors).sort(
      (a, b) => a.lojalitet - b.lojalitet,
    );

    let y = listTop - this.scrollY;
    let bygdHoyde = 0;

    if (forfattere.length === 0) {
      const tomBoks = this.scene.add
        .rectangle(listX, listTop, ROW_WIDTH, 180, COLORS.white, 0.9)
        .setOrigin(0, 0)
        .setStrokeStyle(2, COLORS.oakDark, 0.4);
      const tomTxt = this.scene.add
        .text(
          listX + ROW_WIDTH / 2,
          listTop + 90,
          'Ingen forfattere ennå.\nTilby en kontrakt fra innboksen først.',
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(22),
            color: '#1a1a1a',
            align: 'center',
          },
        )
        .setOrigin(0.5);
      this.layer.add([tomBoks, tomTxt]);
    } else {
      for (const forfatter of forfattere) {
        const rad = this.byggForfatterRad(listX, y, forfatter, snap.dag, snap.kroner);
        listContainer.add(rad);
        y += RAD_HOYDE + RAD_GAP;
        bygdHoyde += RAD_HOYDE + RAD_GAP;
      }
    }

    this.scrollMax = Math.max(0, bygdHoyde - listHoyde);
    if (this.scrollY > this.scrollMax) this.scrollY = this.scrollMax;
    this.bindScroll(listX, listTop, listHoyde);
  }

  private bindScroll(listX: number, listTop: number, listHoyde: number): void {
    if (!this.layer) return;
    if (this.scrollMax <= 0) return;
    const sone = this.scene.add
      .rectangle(listX, listTop, ROW_WIDTH, listHoyde, 0x000000, 0)
      .setOrigin(0, 0)
      .setInteractive();
    let dragStart = 0;
    let scrollStart = 0;
    sone.on('pointerdown', (p: Phaser.Input.Pointer) => {
      dragStart = p.y;
      scrollStart = this.scrollY;
    });
    sone.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!p.isDown) return;
      const delta = dragStart - p.y;
      this.scrollY = Math.max(0, Math.min(this.scrollMax, scrollStart + delta));
      this.render();
    });
    this.layer.add(sone);
  }

  private onWheel = (
    _pointer: Phaser.Input.Pointer,
    _objs: Phaser.GameObjects.GameObject[],
    _dx: number,
    dy: number,
  ): void => {
    if (!this.layer || this.scrollMax <= 0) return;
    this.scrollY = Math.max(0, Math.min(this.scrollMax, this.scrollY + dy));
    this.render();
  };

  private byggForfatterRad(
    x: number,
    y: number,
    forfatter: Author,
    dag: number,
    kroner: number,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);

    const bg = this.scene.add
      .rectangle(0, 0, ROW_WIDTH, RAD_HOYDE, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.35);
    c.add(bg);

    const venstreBorder = this.scene.add
      .rectangle(0, 0, 4, RAD_HOYDE, COLORS.ochre, 1)
      .setOrigin(0, 0);
    c.add(venstreBorder);

    const navn = this.scene.add
      .text(ROW_PADDING, 16, forfatter.navn, {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#1a1a1a',
        wordWrap: { width: ROW_WIDTH - 340 },
      })
      .setOrigin(0, 0);
    c.add(navn);

    const sjanger = this.scene.add
      .text(ROW_PADDING, 70, forfatter.primaerSjanger, {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#3a2e22',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0);
    c.add(sjanger);

    const barLabel = this.scene.add
      .text(ROW_PADDING, 110, `Lojalitet ${forfatter.lojalitet}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#3a2e22',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    c.add(barLabel);

    const barW = 360;
    const barH = 8;
    const barX = ROW_PADDING;
    const barY = 142;
    const barBg = this.scene.add
      .rectangle(barX, barY, barW, barH, COLORS.beigeDark, 1)
      .setOrigin(0, 0);
    const lojFarge =
      forfatter.lojalitet < 30
        ? 0xa05151
        : forfatter.lojalitet < 60
          ? COLORS.ochre
          : COLORS.forest;
    const barFyll = this.scene.add
      .rectangle(barX, barY, (barW * forfatter.lojalitet) / 100, barH, lojFarge, 1)
      .setOrigin(0, 0);
    c.add([barBg, barFyll]);

    const knappW = 300;
    const knappH = 66;
    const knappX = ROW_WIDTH - knappW - 16;
    const knappY = (RAD_HOYDE - knappH) / 2;

    const paaKurs = !!forfatter.paaKurs;
    const sisteDag = forfatter.sisteInvitasjonDag ?? -Infinity;
    const dagerSiden = dag - sisteDag;
    const innenforCooldown = dagerSiden < RESEPSJON_INVITASJON_COOLDOWN_DAGER;
    const harRaad = kroner >= RESEPSJON_INVITASJON_PRIS;

    if (paaKurs) {
      const tagBg = this.scene.add
        .rectangle(knappX, knappY, knappW, knappH, COLORS.beigeDark, 1)
        .setOrigin(0, 0);
      const tagTxt = this.scene.add
        .text(knappX + knappW / 2, knappY + knappH / 2, 'På kurs', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: '#3a2e22',
          fontStyle: '700',
        })
        .setOrigin(0.5);
      c.add([tagBg, tagTxt]);
    } else if (innenforCooldown) {
      const igjen = RESEPSJON_INVITASJON_COOLDOWN_DAGER - dagerSiden;
      const tagBg = this.scene.add
        .rectangle(knappX, knappY, knappW, knappH, COLORS.beigeDark, 1)
        .setOrigin(0, 0);
      const tagTxt = this.scene.add
        .text(
          knappX + knappW / 2,
          knappY + knappH / 2,
          `Invitert nylig (${igjen} dager til)`,
          {
            fontFamily: FONTS.hud,
            fontSize: fontStr(16),
            color: '#3a2e22',
            fontStyle: '700',
            align: 'center',
          },
        )
        .setOrigin(0.5);
      c.add([tagBg, tagTxt]);
    } else {
      const fill = harRaad ? COLORS.forest : 0xcfc6b7;
      const farge = harRaad ? '#ffffff' : '#7a7060';
      const tekst = harRaad
        ? `Inviter (${RESEPSJON_INVITASJON_PRIS.toLocaleString('nb-NO')} kr)`
        : `For lite (${RESEPSJON_INVITASJON_PRIS.toLocaleString('nb-NO')} kr)`;
      const kBg = this.scene.add
        .rectangle(knappX, knappY, knappW, knappH, fill, 1)
        .setOrigin(0, 0);
      const kTxt = this.scene.add
        .text(knappX + knappW / 2, knappY + knappH / 2, tekst, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: farge,
          fontStyle: '700',
        })
        .setOrigin(0.5);
      c.add([kBg, kTxt]);
      if (harRaad) {
        kBg.setInteractive({ useHandCursor: true });
        kBg.on('pointerover', () => kBg.setAlpha(0.85));
        kBg.on('pointerout', () => kBg.setAlpha(1));
        kBg.on('pointerup', () => {
          const ok = gameState.inviterForfatter(forfatter.id);
          if (ok) this.render();
        });
      }
    }

    return c;
  }
}
